/*
  pd-theme.js · shared behaviour for the pd- sections. Vanilla JS, no dependencies.
  Loaded once with `defer` by snippets/pd-head.liquid (rendered in sections/pd-header.liquid).

  Custom elements:  <pd-announcement>  <pd-header>  <pd-cart-drawer>
  Delegated:        form[data-pd-add-form] (AJAX add to cart, opens drawer)
                    [data-pd-cart-open]    (cart icon opens drawer)
                    select[data-pd-sort]   (collection sorting)
                    a[href*="#"]           (same-page anchors such as "/#buy" or "/#faq" scroll when the target
                                            id exists on the current page, so a one-product page never reloads)
  Without JS every piece still works: forms post to /cart/add, the cart icon links to /cart, sort has a submit button.
*/
(function () {
  'use strict';

  var cfg = window.pdTheme || {};
  var routes = Object.assign(
    { root_url: '/', cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update' },
    cfg.routes || {}
  );
  var SECTION = cfg.cartSection || 'pd-cart-drawer';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    format = String(format || cfg.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(n, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (n === null || isNaN(n)) return '0';
      var parts = (n / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  }
  window.pdFormatMoney = formatMoney;

  /* ---------- helpers ---------- */
  var lockCount = 0;
  // While a drawer is open the rest of the document is inert, so a screen reader swipe cannot walk out of
  // the modal into the page behind it (Tab was already trapped, VoiceOver was not). Any element that
  // CONTAINS the open panel is skipped: the cart drawer is rendered inside the header section group.
  var INERT_SEL = 'main, #MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group';
  function setPageInert(on, modalRoot) {
    document.querySelectorAll(INERT_SEL).forEach(function (el) {
      if (on && modalRoot && el.contains(modalRoot)) return;
      el.inert = on;
    });
  }
  function lockScroll(modalRoot) {
    lockCount += 1;
    document.documentElement.classList.add('pd-lock');
    if (lockCount === 1) setPageInert(true, modalRoot);
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount) {
      document.documentElement.classList.remove('pd-lock');
      setPageInert(false, null);
    }
  }
  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }
  function trapFocus(panel, event) {
    if (event.key !== 'Tab') return;
    var items = focusables(panel);
    if (!items.length) { event.preventDefault(); panel.focus(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  function baseUrl() {
    var r = routes.root_url || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* ---------- <pd-announcement> : rotating messages, no timers or countdowns ---------- */
  class PdAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-pd-slide]'));
      if (this.slides.length < 2) return;
      this.index = 0;
      this.delay = Math.max(3, parseInt(this.dataset.interval || '5', 10)) * 1000;
      var self = this;
      this.querySelectorAll('[data-pd-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.go(self.index + Number(btn.getAttribute('data-pd-step')));
          self.restart();
        });
      });
      this.addEventListener('mouseenter', function () { self.stop(); });
      this.addEventListener('mouseleave', function () { self.start(); });
      this.addEventListener('focusin', function () { self.stop(); });
      this.addEventListener('focusout', function () { self.start(); });
      this.start();
    }
    disconnectedCallback() { this.stop(); }
    start() {
      if (reducedMotion || this.timer || !this.slides || this.slides.length < 2) return;
      var self = this;
      this.timer = window.setInterval(function () { self.go(self.index + 1); }, this.delay);
    }
    stop() { window.clearInterval(this.timer); this.timer = null; }
    restart() { this.stop(); this.start(); }
    go(n) {
      var len = this.slides.length;
      this.slides[this.index].hidden = true;
      this.index = (n + len) % len;
      this.slides[this.index].hidden = false;
    }
  }

  /* ---------- <pd-header> : mobile drawer + sticky bar ---------- */
  class PdHeader extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.menu = this.querySelector('[data-pd-menu]');
      this.menuPanel = this.menu ? this.menu.querySelector('[data-pd-menu-panel]') : null;
      this.onKey = function (e) {
        if (e.key === 'Escape') self.closeMenu();
        if (self.menuPanel) trapFocus(self.menuPanel, e);
      };
      this.querySelectorAll('[data-pd-menu-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); self.openMenu(btn); });
      });
      if (this.menu) {
        this.menu.addEventListener('click', function (e) {
          if (e.target.closest('[data-pd-menu-close]')) self.closeMenu();
        });
      }
      if (this.hasAttribute('data-sticky')) this.initSticky();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.menu && this.menu.classList.contains('is-open')) this.closeMenu();
    }
    openMenu(trigger) {
      if (!this.menu || !this.menuPanel) return;
      this.lastFocus = trigger;
      this.menu.classList.add('is-open');
      this.menuPanel.inert = false;
      trigger.setAttribute('aria-expanded', 'true');
      lockScroll(this);
      document.addEventListener('keydown', this.onKey);
      var panel = this.menuPanel;
      window.setTimeout(function () { (focusables(panel)[0] || panel).focus(); }, 60);
    }
    closeMenu() {
      if (!this.menu || !this.menu.classList.contains('is-open')) return;
      this.menu.classList.remove('is-open');
      this.menuPanel.inert = true;
      unlockScroll();
      document.removeEventListener('keydown', this.onKey);
      if (this.lastFocus) { this.lastFocus.setAttribute('aria-expanded', 'false'); this.lastFocus.focus(); }
    }
    initSticky() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      var bar = this.querySelector('[data-pd-header-bar]');
      if (!bar) return;
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;';
      this.prepend(sentinel);
      this.io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (stuck === self.classList.contains('is-stuck')) return;
        self.style.minHeight = stuck ? bar.offsetHeight + 'px' : '';
        self.classList.toggle('is-stuck', stuck);
      });
      this.io.observe(sentinel);
    }
  }

  /* ---------- <pd-cart-drawer> ---------- */
  class PdCartDrawer extends HTMLElement {
    connectedCallback() {
      var self = this;
      window.pdCart = this;
      this.panel = this.querySelector('[data-pd-cart-panel]');
      this.onKey = function (e) {
        if (e.key === 'Escape') self.close();
        if (self.panel) trapFocus(self.panel, e);
      };
      this.addEventListener('click', function (e) { self.onClick(e); });
    }
    disconnectedCallback() {
      if (window.pdCart === this) window.pdCart = null;
      if (this.classList.contains('is-open')) this.close();
    }
    onClick(e) {
      var close = e.target.closest('[data-pd-cart-close]');
      if (close) { e.preventDefault(); this.close(); return; }
      var qty = e.target.closest('[data-pd-qty]');
      if (qty) { e.preventDefault(); this.change(qty.getAttribute('data-key'), parseInt(qty.getAttribute('data-qty'), 10), qty); return; }
      var upsell = e.target.closest('[data-pd-upsell-add]');
      if (upsell) {
        e.preventDefault();
        this.addJson({ items: [{ id: Number(upsell.getAttribute('data-variant-id')), quantity: 1 }] }, upsell);
        return;
      }
      var upgrade = e.target.closest('[data-pd-upgrade]');
      if (upgrade) {
        e.preventDefault();
        this.upgrade(upgrade);
      }
    }
    async upgrade(btn) {
      // Pack upgrade: swap one line (quantity 1) for the next pack of the same size, in one /cart/update.js call.
      if (btn.dataset.pdBusy) return;
      btn.dataset.pdBusy = '1';
      var updates = {};
      updates[btn.getAttribute('data-from')] = 0;
      updates[btn.getAttribute('data-to')] = 1;
      setLoading(btn, true);
      try {
        var data = await this.post(
          routes.cart_update_url + '.js',
          { updates: updates, sections: [SECTION], sections_url: window.location.pathname },
          true
        );
        await this.render(data.sections);
      } catch (err) {
        this.showError(err.message);
      } finally {
        setLoading(btn, false);
        delete btn.dataset.pdBusy;
      }
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.lastFocus = trigger || document.activeElement;
      this.classList.add('is-open');
      if (this.panel) this.panel.inert = false;
      lockScroll(this);
      document.addEventListener('keydown', this.onKey);
      var panel = this.panel;
      window.setTimeout(function () { if (panel) panel.focus(); }, 60);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      if (this.panel) this.panel.inert = true;
      unlockScroll();
      document.removeEventListener('keydown', this.onKey);
      if (this.lastFocus && document.body.contains(this.lastFocus)) this.lastFocus.focus();
    }
    showError(message) {
      var el = this.querySelector('[data-pd-cart-error]');
      if (!el) return;
      el.textContent = message || 'Something went wrong. Please try again.';
      el.hidden = false;
    }
    async post(url, body, isJson) {
      var res = await fetch(url, {
        method: 'POST',
        headers: isJson
          ? { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          : { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: isJson ? JSON.stringify(body) : body
      });
      var data = await res.json().catch(function () { return {}; });
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      return data;
    }
    async addForm(form, submitter) {
      // Guard against a double submit (Enter held down, double tap): one add per click.
      if (form.dataset.pdBusy) return;
      form.dataset.pdBusy = '1';
      var btn = submitter || form.querySelector('[type="submit"]');
      var errorEl = form.querySelector('[data-pd-form-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      setLoading(btn, true);
      try {
        var fd = new FormData(form);
        fd.append('sections', SECTION);
        fd.append('sections_url', window.location.pathname);
        var data = await this.post(routes.cart_add_url + '.js', fd, false);
        await this.render(data.sections);
        this.open(btn);
        document.dispatchEvent(new CustomEvent('pd:cart:added', { detail: data }));
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        else { await this.render(null); this.open(btn); this.showError(err.message); }
      } finally {
        setLoading(btn, false);
        delete form.dataset.pdBusy;
      }
    }
    async addJson(payload, btn) {
      if (btn && btn.dataset.pdBusy) return;
      if (btn) btn.dataset.pdBusy = '1';
      setLoading(btn, true);
      try {
        payload.sections = SECTION;
        payload.sections_url = window.location.pathname;
        var data = await this.post(routes.cart_add_url + '.js', payload, true);
        await this.render(data.sections);
        document.dispatchEvent(new CustomEvent('pd:cart:added', { detail: data }));
      } catch (err) {
        this.showError(err.message);
      } finally {
        setLoading(btn, false);
        if (btn) delete btn.dataset.pdBusy;
      }
    }
    async change(key, quantity, btn) {
      if (!key || isNaN(quantity) || quantity < 0) return;
      if (this.getAttribute('aria-busy') === 'true') return;
      setLoading(btn, true);
      this.setAttribute('aria-busy', 'true');
      try {
        var data = await this.post(
          routes.cart_change_url + '.js',
          { id: key, quantity: quantity, sections: [SECTION], sections_url: window.location.pathname },
          true
        );
        await this.render(data.sections);
      } catch (err) {
        this.showError(err.message);
      } finally {
        setLoading(btn, false);
        this.removeAttribute('aria-busy');
      }
    }
    async render(sections) {
      var html = sections && sections[SECTION];
      if (!html) {
        var res = await fetch(baseUrl() + '?sections=' + encodeURIComponent(SECTION), { headers: { Accept: 'application/json' } });
        var json = await res.json().catch(function () { return {}; });
        html = json[SECTION];
      }
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-pd-cart-inner]');
      var current = this.querySelector('[data-pd-cart-inner]');
      if (!fresh || !current) return;
      current.replaceWith(document.importNode(fresh, true));
      updateCount(parseInt(fresh.getAttribute('data-count') || '0', 10));
    }
  }

  function updateCount(count) {
    document.querySelectorAll('[data-pd-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-pd-cart-open]').forEach(function (el) {
      el.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  function define(name, ctor) { if (!customElements.get(name)) customElements.define(name, ctor); }
  define('pd-announcement', PdAnnouncement);
  define('pd-header', PdHeader);
  define('pd-cart-drawer', PdCartDrawer);

  /* ---------- delegated handlers ---------- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-pd-cart-open]');
    if (!opener || !window.pdCart || cfg.cartDrawer === false) return;
    e.preventDefault();
    window.pdCart.open(opener);
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-pd-add-form]');
    if (!form || !window.pdCart || cfg.cartDrawer === false || !window.fetch) return;
    e.preventDefault();
    window.pdCart.addForm(form, e.submitter);
  });

  document.addEventListener('change', function (e) {
    var select = e.target.closest('select[data-pd-sort]');
    if (select && select.form) select.form.submit();
  });

  // Same-page anchors: "/#buy", "/products/x#faq" or "#how" scroll to the element when it exists on this page
  // (and close the mobile menu first), otherwise the browser follows the link as usual.
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest('a[href*="#"]');
    if (!a || a.target === '_blank') return;
    var url;
    try { url = new URL(a.getAttribute('href'), window.location.href); } catch (err) { return; }
    if (url.origin !== window.location.origin || url.hash.length < 2) return;
    var target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    var header = a.closest('pd-header');
    if (header && header.closeMenu) header.closeMenu();
    window.setTimeout(function () {
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }, header ? 80 : 0);
  });

  // Back/forward cache can show a stale cart count: re-sync when the page is restored.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && window.pdCart) window.pdCart.render(null);
  });
})();
